import React from "react";
import type { DocumentElement as DocumentElementType } from "@eaco/forms-core";

import { cn } from "../lib/utils";
import HiddenElementBanner from "./HiddenElementBanner";

interface Props {
  element: DocumentElementType;
}

const extractLevel = (raw: unknown): string => {
  if (typeof raw === "string" && raw !== "") return raw;
  if (typeof raw === "number") return String(raw);
  if (Array.isArray(raw)) {
    const first = raw[0];
    if (
      first &&
      typeof first === "object" &&
      typeof (first as { value?: unknown }).value === "string"
    ) {
      return (first as { value: string }).value;
    }
  }
  return "2";
};

const Heading: React.FC<Props> = ({ element }) => {
  const data = element.data as {
    label?: string;
    subtitle?: string;
    /** Stored as string ('1'..'4', 'section-label') or rich-entry array */
    level?: unknown;
    hidden?: boolean;
  };
  const label = data?.label || "Heading";
  const subtitle = data?.subtitle;
  const levelKey = extractLevel(data?.level);

  if (levelKey === "section-label") {
    return (
      <div className="space-y-1">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          {label}
        </h3>
        {subtitle && (
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        )}
        <HiddenElementBanner hidden={data?.hidden} />
      </div>
    );
  }

  const rawLevel = Number(levelKey);
  const level = (rawLevel >= 1 && rawLevel <= 4 ? rawLevel : 2) as
    | 1
    | 2
    | 3
    | 4;

  const Tag = `h${level}` as keyof JSX.IntrinsicElements;

  return (
    <div className="space-y-1">
      <Tag
        className={cn(
          "font-semibold",
          level === 1 && "text-2xl",
          level === 2 && "text-xl",
          level === 3 && "text-lg",
          level === 4 && "text-base",
        )}
      >
        {label}
      </Tag>
      {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      <HiddenElementBanner hidden={data?.hidden} />
    </div>
  );
};

export default Heading;
